import { PathNode } from './PathNode';
import { vec3 } from 'gl-matrix';

const MAGIC = 0x4e4e4156; // "NNAV"
const EXPECTED_VERSION = 3;

export class NavigationMeshReader {
  static read(buffer: ArrayBuffer): PathNode[] {
    const view = new DataView(buffer);
    let offset = 0;

    const magic = view.getUint32(offset, true);
    offset += 4;
    if (magic !== MAGIC) {
      throw new Error(`Invalid navmesh file (bad magic: 0x${magic.toString(16)})`);
    }

    const version = view.getUint32(offset, true);
    offset += 4;
    if (version !== EXPECTED_VERSION) {
      throw new Error(`Unsupported navmesh version: ${version}`);
    }

    const nodeCount = view.getUint32(offset, true);
    offset += 4;

    const nodeMap = new Map<number, PathNode>();
    const neighbourIds = new Map<number, number[]>();

    for (let i = 0; i < nodeCount; i++) {
      const id = view.getInt32(offset, true);
      offset += 4;

      const minX = view.getFloat32(offset, true);
      offset += 4;
      const minY = view.getFloat32(offset, true);
      offset += 4;
      const minZ = view.getFloat32(offset, true);
      offset += 4;

      const maxX = view.getFloat32(offset, true);
      offset += 4;
      const maxY = view.getFloat32(offset, true);
      offset += 4;
      const maxZ = view.getFloat32(offset, true);
      offset += 4;

      const refLength = view.getUint32(offset, true);
      offset += 4;
      const refBytes = new Uint8Array(buffer, offset, refLength);
      const reference = new TextDecoder().decode(refBytes);
      offset += refLength;

      const neighbourCount = view.getUint32(offset, true);
      offset += 4;
      const neighbours: number[] = [];
      for (let n = 0; n < neighbourCount; n++) {
        neighbours.push(view.getInt32(offset, true));
        offset += 4;
      }

      const node = new PathNode(
        vec3.fromValues(minX, minY, minZ),
        vec3.fromValues(maxX, maxY, maxZ),
      );

      node.id = id;
      node.reference = reference;

      nodeMap.set(id, node);
      neighbourIds.set(id, neighbours);
    }

    // Resolve neighbour references
    for (const [id, nIds] of neighbourIds) {
      const node = nodeMap.get(id)!;
      for (const nId of nIds) {
        const neighbour = nodeMap.get(nId);
        if (neighbour) {
          node.addNeighbour(neighbour);
        }
      }
    }

    return Array.from(nodeMap.values());
  }
}
